import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { PageLayout } from "../components/Layout";
import { useAuth, API } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import DocumentUpload from "../components/DocumentUpload";
import { 
  FileText, 
  ArrowLeft,
  CheckCircle,
  AlertCircle
} from "lucide-react";

const Documents = () => {
  const { user } = useAuth();
  const [enrollment, setEnrollment] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchEnrollment();
  }, []);

  const fetchEnrollment = async () => {
    try {
      const response = await fetch(`${API}/enrollment/current`, { credentials: 'include' });
      if (response.ok) {
        setEnrollment(await response.json());
      }
    } catch (error) {
      console.error("Error fetching enrollment:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <PageLayout title="Documents">
        <div className="flex items-center justify-center py-20">
          <div className="spinner" />
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout 
      title="Verification Documents"
      subtitle="Upload required documents for your enrollment application."
    >
      <div className="max-w-4xl mx-auto space-y-6" data-testid="documents-page">
        {/* Back to Dashboard */}
        <Link to="/dashboard" className="text-brand-navy text-sm hover:underline flex items-center gap-1">
          <ArrowLeft className="w-4 h-4" /> Back to Dashboard
        </Link>

        {/* Status Banner */}
        {enrollment && (
          <Card className={`card-surface ${enrollment.status === 'submitted' ? 'border-[#10B981]/30' : 'border-[#F59E0B]/30'}`}>
            <CardContent className="py-4">
              <div className="flex items-center gap-4">
                {enrollment.status === 'submitted' ? (
                  <CheckCircle className="w-6 h-6 text-[#10B981]" />
                ) : (
                  <AlertCircle className="w-6 h-6 text-[#F59E0B]" />
                )}
                <div>
                  <p className="font-medium text-slate-900">
                    {enrollment.status === 'submitted' 
                      ? 'Application Submitted' 
                      : 'Application In Progress'}
                  </p>
                  <p className="text-sm text-slate-500">
                    {enrollment.status === 'submitted'
                      ? 'Upload any additional documents to support your application.'
                      : 'Complete your enrollment application and upload required documents.'}
                  </p>
                </div>
                {enrollment.status !== 'submitted' && (
                  <Link to="/apply" className="ml-auto">
                    <Button className="btn-outline text-sm">
                      Continue Application
                    </Button>
                  </Link>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Document Upload Component */}
        <DocumentUpload 
          enrollmentId={enrollment?.enrollment_id} 
          onUploadComplete={() => {}}
        />

        {/* Help Text */}
        <Card className="card-surface">
          <CardContent className="py-6">
            <h3 className="font-medium text-slate-900 mb-4 flex items-center gap-2">
              <FileText className="w-5 h-5 text-brand-navy" />
              Document Guidelines
            </h3>
            <ul className="space-y-3 text-sm text-slate-500">
              <li className="flex items-start gap-2">
                <span className="text-brand-navy">•</span>
                <span>Documents must be clear and legible</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-brand-navy">•</span>
                <span>Accepted formats: JPG, PNG, GIF, WebP, PDF</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-brand-navy">•</span>
                <span>Maximum file size: 10MB per document</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-brand-navy">•</span>
                <span>Proof of address must be dated within 90 days</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-brand-navy">•</span>
                <span>All personal information should be visible and not obscured</span>
              </li>
            </ul>
          </CardContent>
        </Card>
      </div>
    </PageLayout>
  );
};

export default Documents;
